import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Living room and hall: walls and ceiling, 640 sq ft of wall, two coats eggshell', amount: '$1,450' },
  { item: 'Prep: fill nail holes, tape and mud two ceiling cracks, sand, spot-prime patches', amount: '$280' },
  { item: 'Baseboard, casing and six six-panel doors, semi-gloss, brushed', amount: '$620' },
  { item: 'Optional: accent wall in a deep color (extra coat for coverage)', amount: '$190' },
];

const faq = [
  {
    q: 'How do I write a painting estimate a homeowner will actually sign?',
    a: 'Break the job into rooms or elevations, give the prep its own line, name the product and the sheen, and say how many coats. Homeowners sign faster when they can see that the cheaper bid skipped the patching and priming that yours includes.',
  },
  {
    q: 'Should colors be chosen before or after the estimate is signed?',
    a: 'Either works, but write down which. A common approach is to price by surface and sheen, then confirm colors before the start date, with a note that a change of color after paint is bought may cost extra.',
  },
  {
    q: 'Can a homeowner pick extras like an accent wall on the estimate?',
    a: 'Yes. In Scopie you can mark a line as optional. The homeowner ticks the extras they want when they open the estimate, and the total updates in front of them before they sign.',
  },
  {
    q: 'Does Scopie work out gallons or labor hours for me?',
    a: 'No. Scopie has no paint or labor calculator. You price the work the way you always have; Scopie turns that into a clean estimate, a signed agreement, a job on the calendar and an invoice.',
  },
];

const page: IndustryPage = {
  slug: 'painting',
  order: 4,
  group: 'Home services',
  tile: 'painting',
  path: '/for/painting',
  primaryKeyword: 'painting business software',
  secondaryKeywords: ['painting estimate software', 'painting contractor software', 'how to estimate a paint job'],
  title: 'Painting Business Software for Estimates | Scopie', // 50
  metaDescription:
    'Write painting estimates by room, surface and coat, offer extras the homeowner can tick, and get the estimate signed on their phone before you buy paint.', // 153
  h1: 'Estimates for painting contractors, room by room',
  answerFirst:
    'Scopie helps painting contractors turn a walk-through into an estimate the homeowner can read: each room, the prep, the product and the number of coats on its own line. Copy the link, text it over, and the homeowner signs it on their phone. Then put the job on the calendar and invoice it.',
  exampleQuote: { label: 'Sample interior estimate', lines, total: '$2,540', depositNote: 'Deposit and color-change terms come from the painter\'s own clauses.' },
  blocks: [
    {
      type: 'text',
      id: 'walkthrough',
      h2: 'From the walk-through to a written estimate the same evening',
      lead: 'They had three painters through the house this week. The one who sends a clear number tonight gets the call.',
      paragraphs: [
        'Most painting jobs are lost between the walk-through and the estimate. You measured the walls, noticed the water stain on the ceiling and the peeling trim, and then the notes sat in the truck for three days.',
        'In Scopie you build the estimate from your own items: walls by room, ceilings, trim, doors, cabinets, exterior elevations. Write the prep in plain words, such as "scrape and prime bare wood on the south fascia", so the homeowner understands why your number is higher than the painter who will paint over it.',
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'What an interior painting estimate can look like',
      label: 'Sample interior estimate, two-storey colonial',
      lines,
      total: '$2,540',
      totalLabel: 'Total if the accent wall is chosen',
      note: 'Figures are only an example. The accent wall is an optional line the homeowner can tick or leave.',
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Six estimate mistakes that cost painters money',
      style: 'mistakes',
      items: [
        { title: 'Prep hidden inside the wall price', text: 'When patching and priming are buried, the homeowner compares your total with a bid that does none of it, and yours looks expensive.' },
        { title: 'No coat count', text: '"Paint living room" invites an argument when the second coat is needed over a dark color.' },
        { title: 'Sheen and product left blank', text: 'Write the product line and sheen, or expect a request to switch to a premium paint at your cost.' },
        { title: 'Furniture and floors not mentioned', text: 'Say who moves furniture, who takes down pictures, and how floors are covered.' },
        { title: 'Surprise rot or failing plaster', text: 'Exterior jobs uncover soft wood. Say that repairs found after scraping are priced separately before you do them.' },
        { title: 'An estimate with no end date', text: 'Paint prices move every spring. Give the estimate a validity date.' },
      ],
    },
    {
      type: 'text',
      id: 'seasons',
      h2: 'Exterior season and interior season need different terms',
      paragraphs: [
        'Exterior work depends on temperature, dew point and rain, so homeowners need to know what happens when you lose a day to weather: you move to the next dry day and the price stays the same. Interior work fills the winter calendar, when furniture, pets and people are all in the house, so access hours and room order belong in the estimate.',
        'Save those two sets of terms once in your clause library, and pull the right one into each estimate rather than retyping them.',
      ],
    },
    {
      type: 'list',
      id: 'questions',
      h2: 'What homeowners ask before they sign a painting estimate',
      style: 'bullets',
      items: [
        { text: 'How many days will you be in the house, and which rooms first?' },
        { text: 'Is the ceiling included, or just the walls?' },
        { text: 'Who buys the paint, and can I choose the brand?' },
        { text: 'What happens if I change a color after you start?' },
        { text: 'Do you fix the cracks or paint over them?' },
      ],
    },
    {
      type: 'text',
      id: 'signed',
      h2: 'Signed on the homeowner\'s phone, then on your calendar',
      paragraphs: [
        'Every estimate has its own link with your company name at the top. Text it from your phone; the homeowner opens it, ticks any extras, reads your terms and signs. Nothing to install.',
        'Once it is signed, schedule the job and put the painters and the sprayer on it. Your painters see where they are going and when, without seeing what the house is paying. When the last coat is dry, the invoice has its own link to share the same way.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Painting estimates: common questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/pressure-washing', anchor: 'Scopie for pressure washing businesses', status: 'live' },
    { href: '/for/flooring', anchor: 'Scopie for flooring contractors', status: 'live' },
    { href: '/templates/painting-estimate-template', anchor: 'free painting estimate template', status: 'live' },
  ],
  cta: {
    heading: 'Price your next walk-through in Scopie',
    text: 'Set up rooms, prep and coats as your own items once, and every painting estimate after that takes minutes.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
